/**
 * The guest's own two flights, live (D71).
 *
 * The server does all the deciding — which flight under the number is theirs,
 * how often FlightAware is asked, what to do when it cannot be — and caches
 * the answer per flight. So asking it is cheap, and this asks every five
 * minutes while the page is visible, and once more on coming back to it,
 * which is what a guest walking off a plane does.
 */
import { useEffect, useState } from 'react';
import type { FlightStatus, FlightsResponse } from '@mv/contracts';
import { api } from './api';

export const FLIGHT_POLL_MS = 5 * 60_000;

/** `/me/flights`, kept fresh while the page is open. Undefined until the first
 *  answer, and left as it was when a later one fails. */
export function useFlights(enabled: boolean): FlightsResponse | undefined {
  const [flights, setFlights] = useState<FlightsResponse>();
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const pull = () => {
      if (cancelled || document.hidden) return;
      api<FlightsResponse>('/me/flights')
        .then((f) => { if (!cancelled) setFlights(f); })
        .catch(() => {});
    };
    pull();
    const id = setInterval(pull, FLIGHT_POLL_MS);
    const onVisible = () => { if (!document.hidden) pull(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
  return flights;
}

/** The resort's zone. A time anywhere in it is printed bare; anywhere else
 *  carries its airport, since "lands 15:05" in Singapore is not 15:05 here. */
const HOME_TZ = 'Asia/Bangkok';

function clock(iso: string, tz: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz ?? HOME_TZ })
      .format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: HOME_TZ })
      .format(new Date(iso));
  }
}

/** Late enough to say so. Airlines call anything under fifteen minutes on
 *  time, and so does every departures board a guest will look at. */
export const LATE_MINUTES = 15;

export type FlightTone = 'ok' | 'late' | 'bad' | 'done';

/**
 * One short line for under the date: what is happening and when, in the
 * local time of the airport it is happening at. Undefined when there is
 * nothing live to say, and the card shows the number and its link alone.
 *
 * Short on purpose: it sits in a third of a phone's width.
 */
export function flightLine(f: FlightStatus | undefined, direction: 'arrival' | 'departure'):
  { text: string; late?: string; tone: FlightTone } | undefined {
  if (!f?.live || !f.phase) return undefined;
  if (f.phase === 'cancelled') return { text: 'Cancelled', tone: 'bad' };
  if (f.phase === 'diverted') return { text: 'Diverted', tone: 'bad' };

  const end = direction === 'arrival' ? f.lands : f.departs;
  const at = (iso: string | undefined) => {
    if (!iso) return '';
    const away = end?.tz && end.tz !== HOME_TZ && end.airport ? ` ${end.airport}` : '';
    return `${clock(iso, end?.tz)}${away}`;
  };
  const late = (f.delayMinutes ?? 0) >= LATE_MINUTES;
  // Its own line under the time rather than appended to it: "Lands 21:59 ·
  // 40 min late" wraps mid-phrase in a third of a phone.
  const lateBy = late ? { late: `${f.delayMinutes} min late` } : {};

  if (direction === 'arrival') {
    if (f.phase === 'landed') return { text: `Landed ${at(end?.actual ?? end?.estimated)}`, tone: 'done' };
    const when = at(end?.estimated ?? end?.scheduled);
    return when ? { text: `Lands ${when}`, ...lateBy, tone: late ? 'late' : 'ok' } : undefined;
  }
  if (f.phase === 'en-route' || f.phase === 'landed') {
    return { text: `Left ${at(end?.actual ?? end?.estimated)}`, tone: 'done' };
  }
  const when = at(end?.estimated ?? end?.scheduled);
  return when ? { text: `Leaves ${when}`, ...lateBy, tone: late ? 'late' : 'ok' } : undefined;
}
